import useSWR from "swr";
import { HttpError } from "@/api/apiFetch";
import { getCourseCompletionQuizResult } from "@/api/generated/course-completion-quiz/course-completion-quiz";
import { getEnrollment } from "@/api/generated/enrollments/enrollments";
import type { CompletionQuizResult, Enrollment } from "@/api/generated/model";
import { resultScreenFor } from "@/lib/courseCompletionQuiz";

/** How often the result is asked for while the last answers are still graded. */
const WAIT_FOR_RESULT_MS = 1500;

type ResultScreen = ReturnType<typeof resultScreenFor>;

interface CourseCompletionQuizEvaluation {
  enrollment: Enrollment | undefined;
  /** The result of the last finished run, or null while none has finished. */
  result: CompletionQuizResult | null;
  /** Which screen the evaluation page shows, once the enrollment is loaded. */
  screen: ResultScreen | null;
  isLoading: boolean;
  error: Error | undefined;
}

/** The backend answers 422 while no run has finished yet. */
const resultOrNull = async (
  enrollmentId: string
): Promise<CompletionQuizResult | null> => {
  try {
    return await getCourseCompletionQuizResult(enrollmentId);
  } catch (failure) {
    if (failure instanceof HttpError && failure.status === 422) return null;
    throw failure;
  }
};

// The enrollment says how many runs there are; the result alone cannot tell
// an earlier attempt from the one that has just been graded. One fetcher for
// both keeps the polling below looking at a consistent pair.
const loadEnrollmentAndResult = async (enrollmentId: string) => {
  const [enrollment, result] = await Promise.all([
    getEnrollment(enrollmentId),
    resultOrNull(enrollmentId),
  ]);
  return { enrollment, result };
};

type Loaded = Awaited<ReturnType<typeof loadEnrollmentAndResult>>;

const screenOf = (loaded: Loaded | undefined): ResultScreen | null => {
  const quiz = loaded?.enrollment.courseCompletionQuiz;
  return quiz ? resultScreenFor(quiz, loaded.result) : null;
};

/**
 * Loads the enrollment together with the result of its last finished quiz run
 * for the evaluation page, and keeps asking while the last answers are still
 * being graded.
 */
const useCourseCompletionQuizEvaluation = (
  enrollmentId: string | undefined
): CourseCompletionQuizEvaluation => {
  const { data, error, isLoading } = useSWR(
    enrollmentId
      ? (["courseCompletionQuizScreen", enrollmentId] as const)
      : null,
    ([, id]) => loadEnrollmentAndResult(id),
    {
      // The run ends when its last answer has been graded; until then keep asking.
      refreshInterval: (latest) =>
        screenOf(latest) === "wait" || !latest ? WAIT_FOR_RESULT_MS : 0,
      revalidateOnFocus: false,
      dedupingInterval: 0,
    }
  );

  return {
    enrollment: data?.enrollment,
    result: data?.result ?? null,
    screen: screenOf(data),
    isLoading,
    error,
  };
};

export { useCourseCompletionQuizEvaluation };
